import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, CheckIcon, XIcon, InfoIcon } from 'lucide-react';
import { BOWL_OPTIONS } from '../data/menuData';
import { ProductOption, CartItemType } from '../types/types';
import { CONFIG } from '../data/config';
interface BowlBuilderProps {
  onClose: () => void;
  onSave: (item: CartItemType) => void;
  initialItem?: CartItemType;
}
const STEPS = [{
  id: 'base',
  title: 'Elegí la base',
  subtitle: 'Obligatorio — elegí una base para tu bowl',
  required: true,
  multi: false,
  options: BOWL_OPTIONS.bases
}, {
  id: 'proteina',
  title: 'Sumá tu proteína',
  subtitle: 'Opcional — podés avanzar sin proteína si preferís',
  required: false,
  multi: false,
  options: BOWL_OPTIONS.proteinas
}, {
  id: 'toppings',
  title: 'Toppings frescos',
  subtitle: 'Opcional — elegí los que quieras o avanzá sin ninguno',
  required: false,
  multi: true,
  options: BOWL_OPTIONS.toppings
}, {
  id: 'toppingsPremium',
  title: 'Toppings premium',
  subtitle: 'Opcional — tienen costo adicional por unidad',
  required: false,
  multi: true,
  options: BOWL_OPTIONS.toppingsPremium
}, {
  id: 'aderezos',
  title: 'Aderezo',
  subtitle: 'Opcional — elegí los que más te gusten',
  required: false,
  multi: true,
  options: BOWL_OPTIONS.aderezos,
  note: 'Consultar opciones disponibles'
}, {
  id: 'semillas',
  title: 'Semillas',
  subtitle: 'Opcional — le dan un toque crunchy a tu bowl',
  required: false,
  multi: true,
  options: BOWL_OPTIONS.semillas
}, {
  id: 'comentarios',
  title: 'Comentarios',
  subtitle: 'Opcional — si no tenés comentarios, podés agregar tu bowl directamente',
  required: false,
  multi: false,
  options: []
}];
export function BowlBuilder({
  onClose,
  onSave,
  initialItem
}: BowlBuilderProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [selections, setSelections] = useState<any>(initialItem?.selections || {
    base: undefined,
    proteina: undefined,
    toppings: [],
    toppingsPremium: [],
    aderezos: [],
    semillas: [],
    comentarios: ''
  });
  const step = STEPS[currentStep];
  const handleSelect = (option: ProductOption) => {
    if (step.multi) {
      const currentList = selections[step.id] || [];
      const exists = currentList.find((item: ProductOption) => item.id === option.id);
      if (exists) {
        setSelections({
          ...selections,
          [step.id]: currentList.filter((item: ProductOption) => item.id !== option.id)
        });
      } else {
        setSelections({
          ...selections,
          [step.id]: [...currentList, option]
        });
      }
    } else {
      if (selections[step.id]?.id === option.id && !step.required) {
        setSelections({
          ...selections,
          [step.id]: undefined
        }); // Deselect if not required
      } else {
        setSelections({
          ...selections,
          [step.id]: option
        });
      }
    }
  };
  const isStepValid = () => {
    if (step.required && !selections[step.id]) return false;
    return true;
  };
  const nextStep = () => {
    if (isStepValid() && currentStep < STEPS.length - 1) {
      setCurrentStep(prev => prev + 1);
    }
  };
  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };
  const calculateSubtotal = () => {
    let total = 0;
    if (selections.base) total += selections.base.price;
    if (selections.proteina) total += selections.proteina.price;
    total += (selections.toppings || []).reduce((sum: number, t: ProductOption) => sum + t.price, 0);
    total += (selections.toppingsPremium || []).reduce((sum: number, t: ProductOption) => sum + t.price, 0);
    total += (selections.aderezos || []).reduce((sum: number, t: ProductOption) => sum + t.price, 0);
    total += (selections.semillas || []).reduce((sum: number, t: ProductOption) => sum + t.price, 0);
    return total;
  };
  const handleSave = () => {
    if (!selections.base) return; // Safety check
    const item: CartItemType = {
      id: initialItem?.id || `bowl_${Date.now()}`,
      productId: 'custom_bowl',
      name: 'Bowl Personalizado',
      basePrice: 0,
      quantity: initialItem?.quantity || 1,
      selections,
      subtotal: calculateSubtotal(),
      type: 'bowl'
    };
    onSave(item);
  };
  // Get selection count for current step
  const getSelectionCount = () => {
    const val = selections[step.id];
    if (Array.isArray(val)) return val.length;
    if (val) return 1;
    return 0;
  };
  return <motion.div initial={{
    opacity: 0,
    y: 50
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    y: 50
  }} className="fixed inset-0 z-50 bg-background flex flex-col max-w-md mx-auto shadow-2xl" data-id="element-14">
      {/* Header */}
      <div className="bg-white px-4 py-4 flex items-center justify-between shadow-sm z-10" data-id="element-15">
        <button onClick={onClose} className="p-2 -ml-2 text-gray-500 hover:text-gray-900" data-id="element-16">
          <XIcon className="w-6 h-6" data-id="element-17" />
        </button>
        <h2 className="font-heading font-semibold text-lg" data-id="element-18">Armá tu Bowl</h2>
        <div className="w-10" data-id="element-19"></div> {/* Spacer for centering */}
      </div>

      {/* Progress Bar */}
      <div className="bg-white px-4 pb-4" data-id="element-20">
        <div className="flex justify-between mb-2" data-id="element-21">
          {STEPS.map((s, idx) => <div key={s.id} className={`h-1.5 flex-1 mx-0.5 rounded-full ${idx <= currentStep ? 'bg-verde' : 'bg-gray-200'}`} data-id="element-22" />)}
        </div>
        <div className="text-sm text-gray-500 text-center font-medium" data-id="element-23">
          Paso {currentStep + 1} de {STEPS.length}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 pb-24" data-id="element-24">
        <AnimatePresence mode="wait" data-id="element-25">
          <motion.div key={currentStep} initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} transition={{
          duration: 0.2
        }} data-id="element-26">
            {/* Title + Badge */}
            <div className="flex items-start justify-between gap-2 mb-1" data-id="element-27">
              <h3 className="text-2xl font-heading font-bold text-gray-900" data-id="element-28">
                {step.title}
              </h3>
              <span className={`text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap mt-1 ${step.required ? 'bg-terracota/10 text-terracota' : 'bg-gray-100 text-gray-500'}`} data-id="element-29">
                {step.required ? 'Obligatorio' : 'Opcional'}
              </span>
            </div>

            {/* Subtitle hint */}
            <p className="text-gray-400 text-sm mb-3" data-id="element-30">{step.subtitle}</p>

            {/* Selection count for multi-select */}
            {step.multi && step.options.length > 0 && <div className="flex items-center gap-2 mb-3" data-id="element-31">
                <span className="text-sm font-medium text-verde-dark bg-verde/10 px-3 py-1 rounded-full" data-id="element-32">
                  {getSelectionCount()} seleccionado
                  {getSelectionCount() !== 1 ? 's' : ''}
                </span>
                {getSelectionCount() === 0 && <span className="text-xs text-gray-400 italic" data-id="element-33">
                    Podés avanzar sin elegir
                  </span>}
              </div>}

            {/* Single select hint */}
            {!step.multi && !step.required && step.options.length > 0 && <div className="flex items-center gap-2 mb-3" data-id="element-34">
                {selections[step.id] ? <span className="text-sm font-medium text-verde-dark bg-verde/10 px-3 py-1 rounded-full" data-id="element-35">
                    ✓ Seleccionado — tocá de nuevo para quitar
                  </span> : <span className="text-xs text-gray-400 italic" data-id="element-36">
                    Podés avanzar sin elegir
                  </span>}
              </div>}

            {step.note && <p className="text-terracota text-sm mb-4 font-medium" data-id="element-37">
                {step.note}
              </p>}

            {step.id === 'comentarios' ? <div data-id="element-38">
                <textarea value={selections.comentarios} onChange={e => setSelections({
              ...selections,
              comentarios: e.target.value
            })} placeholder="Ejemplo: sin cebolla, alérgico a lácteos, cambiar tomate por pepino, etc." className="w-full h-32 p-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-verde focus:border-transparent resize-none" data-id="element-39" />
                <p className="text-xs text-gray-400 mt-2 flex items-center gap-1" data-id="element-40">
                  <InfoIcon className="w-3 h-3" data-id="element-41" />
                  Si no tenés comentarios, simplemente tocá "Agregar" abajo
                </p>
              </div> : <div className="space-y-3" data-id="element-42">
                {step.options.map(option => {
              const isSelected = step.multi ? selections[step.id]?.some((item: ProductOption) => item.id === option.id) : selections[step.id]?.id === option.id;
              return <button key={option.id} onClick={() => handleSelect(option)} className={`w-full flex items-center justify-between p-4 rounded-xl border-2 transition-all ${isSelected ? 'border-verde bg-verde/5' : 'border-gray-100 bg-white hover:border-gray-200'}`} data-id="element-43">
                      <span className={`font-medium ${isSelected ? 'text-verde-dark' : 'text-gray-700'}`} data-id="element-44">
                        {option.name}
                      </span>
                      <div className="flex items-center gap-3" data-id="element-45">
                        {option.price > 0 && <span className="text-sm text-gray-500" data-id="element-46">
                            +{CONFIG.CURRENCY_SYMBOL}
                            {option.price}
                          </span>}
                        {option.price === 0 && <span className="text-xs text-gray-400" data-id="element-47">
                            Incluido
                          </span>}
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center border ${isSelected ? 'bg-verde border-verde text-white' : 'border-gray-300'}`} data-id="element-48">
                          {isSelected && <CheckIcon className="w-4 h-4" data-id="element-49" />}
                        </div>
                      </div>
                    </button>;
            })}
              </div>}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Footer */}
      <div className="bg-white border-t border-gray-100 p-4 flex items-center justify-between gap-3 sticky bottom-0" style={{
      paddingBottom: 'max(16px, env(safe-area-inset-bottom, 16px))'
    }} data-id="element-50">
        {currentStep > 0 ? <button onClick={prevStep} className="p-3 rounded-xl bg-gray-100 text-gray-600 active:bg-gray-200 transition-colors min-w-[48px] min-h-[48px] flex items-center justify-center" data-id="element-51">
            <ChevronLeftIcon className="w-6 h-6" data-id="element-52" />
          </button> : <div className="w-[48px]" data-id="element-53"></div>}

        <div className="flex-1 flex flex-col items-center" data-id="element-54">
          <span className="text-xs text-gray-500 uppercase tracking-wider font-semibold" data-id="element-55">
            Subtotal
          </span>
          <span className="font-bold text-lg text-gray-900" data-id="element-56">
            {CONFIG.CURRENCY_SYMBOL}
            {calculateSubtotal().toLocaleString()}
          </span>
        </div>

        {currentStep < STEPS.length - 1 ? <button onClick={nextStep} disabled={!isStepValid()} className={`px-5 py-3 rounded-xl font-semibold flex items-center gap-2 transition-colors min-h-[48px] ${isStepValid() ? 'bg-verde text-white active:bg-verde-dark shadow-md shadow-verde/20' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`} data-id="element-57">
            {!step.required && getSelectionCount() === 0 ? 'Saltar' : 'Siguiente'}{' '}
            <ChevronRightIcon className="w-5 h-5" data-id="element-58" />
          </button> : <button onClick={handleSave} className="px-5 py-3 rounded-xl font-semibold flex items-center gap-2 bg-terracota text-white active:bg-[#9a3a18] shadow-md shadow-terracota/20 transition-colors min-h-[48px]" data-id="element-59">
            Agregar <CheckIcon className="w-5 h-5" data-id="element-60" />
          </button>}
      </div>
    </motion.div>;
}